import * as React from 'react';
import { FormattedMessage, useIntl, MessageDescriptor } from 'react-intl';

import {
  GiftIcon,
  Tooltip,
  ModalContext,
  TooltipModel,
  ReactFCC,
} from '@notino/react-styleguide';

import { AB_TEST } from '@abTests';
import { useNewPdDesignEnabled } from '@containers/ProductDetailContainer/ProductDetail/hooks/useNewPdDesignEnabled';
import { isAbTestEnabled } from '@helpers/utils';

import { useAbTest } from '../../hooks/useAbTest/useAbTest';
import { LeftIconWrapper } from '../styled';

import { GiftImage } from './GiftImage';
import { GiftImageThumb } from './GiftImageThumb';
import {
  Container,
  FlexWrapper,
  Content,
  Title,
  IconTextWrapper,
} from './styled';

interface IGiftBaseProps {
  containerId: string;
  isGiftImage: boolean;
  titleMessage: MessageDescriptor;
  imageUrl: string;
  imageMessage: MessageDescriptor;
}

export const GiftBase: ReactFCC<IGiftBaseProps> = React.memo(
  ({
    imageUrl,
    imageMessage,
    containerId,
    isGiftImage,
    titleMessage,
    children,
  }) => {
    const abTestValue = useAbTest();
    const hideGiftIcon = isAbTestEnabled(
      abTestValue,
      AB_TEST.FREE_GIFT_POSITION,
      '1'
    );
    const { formatMessage } = useIntl();
    const { toggleModal } = ModalContext.useModalContext();
    const newPdDesignEnabled = useNewPdDesignEnabled();

    const giftImage = React.useMemo(
      () => (
        <GiftImage
          formattedImageMessage={formatMessage(imageMessage)}
          imageUrl={imageUrl}
        />
      ),
      [imageUrl, imageMessage, formatMessage]
    );

    const openModal = React.useCallback(() => {
      toggleModal(giftImage);
    }, [giftImage, toggleModal]);

    return (
      <Container id={containerId}>
        <FlexWrapper newDesign={newPdDesignEnabled} hideGiftIcon={hideGiftIcon}>
          <IconTextWrapper newDesign={newPdDesignEnabled}>
            {!hideGiftIcon && (
              <LeftIconWrapper>
                <GiftIcon width="20px" height="20px" />
              </LeftIconWrapper>
            )}
            <Content>
              <Title
                giftImage={isGiftImage}
                newPdDesignEnabled={newPdDesignEnabled}
              >
                <FormattedMessage {...titleMessage} />
              </Title>
              {children}
            </Content>
          </IconTextWrapper>
          {isGiftImage && (
            <>
              <Tooltip
                content={giftImage}
                position={TooltipModel.Position.topLeft}
                alignDesktop={true}
              >
                <GiftImageThumb
                  touch={false}
                  imageUrl={imageUrl}
                  formattedImageMessage={formatMessage(imageMessage)}
                />
              </Tooltip>
              <GiftImageThumb
                touch={true}
                imageUrl={imageUrl}
                formattedImageMessage={formatMessage(imageMessage)}
                onClick={openModal}
              />
            </>
          )}
        </FlexWrapper>
      </Container>
    );
  }
);
